/**
 * 剪貼簿寫入 helper — 優先 navigator.clipboard，失敗則用 textarea + execCommand fallback。
 *
 * 回傳 boolean 指示是否成功，呼叫者自行決定 toast 文案。
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Modern browsers + secure context
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // 繼續嘗試 fallback（例如 iframe / non-secure context）
    }
  }
  // Fallback：textarea + execCommand
  if (typeof document === 'undefined') return false;
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    textarea.setAttribute('readonly', '');
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

/**
 * 組裝新增會員後給操作員轉貼用的訊息模板。
 * 不含 BO 內部資訊（URL / token / IP），避免敏感資訊外洩。
 */
export function buildNewMemberMessage(params: {
  brandLabel: string;
  username: string;
  password: string;
  tierName?: string;
}): string {
  const lines = [
    `您的 ${params.brandLabel} VPN 帳號已建立`,
    `使用者:${params.username}`,
    `密碼:${params.password}`,
  ];
  if (params.tierName) {
    lines.push(`方案:${params.tierName}`);
  }
  return lines.join('\n');
}

/**
 * 客戶端日誌一筆 record 的最小欄位集合。
 *
 * 對應後端 vpn_client_log entity + frontend 的 ClientLogRecord interface。
 * 沒有的欄位顯示為「(無資料)」，operator 拿到 export 後可以直接 grep / 轉貼。
 */
export interface ClientLogCopyRecord {
  id: number | string;
  userId?: number | string | null;
  username?: string | null;
  brand?: string | null;
  trigger?: string | null;
  platform?: string | null;
  appVersion?: string | null;
  osVersion?: string | null;
  deviceModel?: string | null;
  networkType?: string | null;
  vpnStatus?: string | null;
  lastError?: string | null;
  securityFlags?: string | null;
  // 2.0.53 S53-4 — user 上傳前打的主觀回報文字 (輔助佐證,非 ground-truth)
  userFeedbackText?: string | null;
  requestId?: string | null;
  logStartTime?: string | Date | null;
  logEndTime?: string | Date | null;
  createdAt?: string | Date | null;
  appLog?: string | null;
  singboxLog?: string | null;
  // 容許未來新增 signal / connectivity payload。物件 / 字串都接受，輸出時格式化。
  signal?: unknown;
  [key: string]: unknown;
}

const NULL_LABEL = '（無資料）';

function formatScalar(value: unknown): string {
  if (value === null || value === undefined) return NULL_LABEL;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return NULL_LABEL;
    return value.toISOString();
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length === 0 ? NULL_LABEL : trimmed;
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function formatLargeText(value: unknown): string {
  if (value === null || value === undefined) return NULL_LABEL;
  if (typeof value === 'string') {
    return value.length === 0 ? NULL_LABEL : value;
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function formatSignal(signal: unknown): string {
  if (signal === null || signal === undefined) return NULL_LABEL;
  if (typeof signal === 'string') {
    const trimmed = signal.trim();
    if (trimmed.length === 0) return NULL_LABEL;
    // try parse JSON 字串再 pretty print；parse 失敗保留原字串
    try {
      const parsed = JSON.parse(trimmed);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return trimmed;
    }
  }
  if (typeof signal === 'object') {
    try {
      return JSON.stringify(signal, null, 2);
    } catch {
      return String(signal);
    }
  }
  return String(signal);
}

/**
 * 把一筆 client log（metadata + appLog + singboxLog + signal）格式化成可貼到工單 / IM 的純文字。
 *
 * - metadata 段集中所有 scalar 欄位（時間、客戶、平台、觸發、裝置、版本、網路、VPN 狀態、最後錯誤、安全標記）
 * - 分 3 個大段落：App / Sing-box / Signal
 * - null / undefined / 空字串顯示「（無資料）」
 * - signal 若是 JSON 字串/物件則 pretty print 2-indent
 */
export function buildClientLogExport(record: ClientLogCopyRecord): string {
  const headerLines = [
    '=== Client Log Export ===',
    `時間: ${formatScalar(record.createdAt)}`,
    `ID: ${formatScalar(record.id)}`,
    `客戶: ${record.username && String(record.username).trim().length > 0 ? `${String(record.username).trim()} (#${formatScalar(record.userId)})` : `#${formatScalar(record.userId)}`}`,
    `品牌: ${formatScalar(record.brand)}`,
    `平台: ${formatScalar(record.platform)} / ${formatScalar(record.osVersion)}`,
    `App 版本: ${formatScalar(record.appVersion)}`,
    `裝置: ${formatScalar(record.deviceModel)}`,
    `網路: ${formatScalar(record.networkType)}`,
    `VPN 狀態: ${formatScalar(record.vpnStatus)}`,
    `觸發: ${formatScalar(record.trigger)}`,
    `Request ID: ${formatScalar(record.requestId)}`,
    `日誌時段: ${formatScalar(record.logStartTime)} → ${formatScalar(record.logEndTime)}`,
    `最後錯誤: ${formatScalar(record.lastError)}`,
    `安全標記: ${formatScalar(record.securityFlags)}`,
    // 2.0.53 S53-4 — user 上傳前打的主觀回報文字 (輔助佐證,非 ground-truth)
    `用戶回報: ${formatScalar(record.userFeedbackText)}`,
    '========================',
  ];

  const sections = [
    headerLines.join('\n'),
    '',
    '=== App Log ===',
    formatLargeText(record.appLog),
    '',
    '=== Sing-box Log ===',
    formatLargeText(record.singboxLog),
    '',
    '=== Connectivity Signal ===',
    formatSignal(record.signal),
  ];

  return sections.join('\n');
}

/**
 * 一鍵把 client log record 寫進剪貼簿。回傳是否成功，呼叫者自行決定 toast 文案。
 */
export async function copyClientLogToClipboard(
  record: ClientLogCopyRecord
): Promise<boolean> {
  return copyToClipboard(buildClientLogExport(record));
}
